import { getAuthUser } from "../../utils/auth";
import { getModuleGrant, hasModulePermission } from "../../utils/moduleAccess";

const SUPER_PRIVILEGED_ROLES = new Set(["SUPER_ADMIN", "ADMIN"]);
const SALES_IDENTIFIERS = new Set(["SALES"]);
const ONM_IDENTIFIERS = new Set([
  "ONM",
  "ONM TEAM",
  "O&M",
  "O&M TEAM",
  "O & M",
  "OPERATION AND MAINTENANCE",
  "OPERATIONS AND MAINTENANCE",
  "OPERATION & MAINTENANCE",
  "OPERATIONS & MAINTENANCE",
]);
const TECHNICAL_IDENTIFIERS = new Set(["TECHNICAL"]);
const PURCHASE_IDENTIFIERS = new Set(["PURCHASE", "PROCUREMENT"]);
const HEAD_OFFICE_TEAMS = new Set([
  "ACCOUNTS", "IT", "HRD", "CUSTOMER CARE", "STORE",
  "DESIGNER", "NOC", "TECHOPS", "SERVICE VENDOR", "RETENSION",
  "PURCHASE", "VAS", "ONM", "PROJECT", "MARKETING", "OPERATIONS",
  "SUPPORT", "ADMIN", "COLLECTION", "PROCUREMENT", "FEASIBILITY",
  "QUALITY", "TRAINING", "COMPLIANCE", "FINANCE",
]);
const TECHNICAL_BRANCH_INCHARGE_DESIGNATIONS = new Set([
  "BRANCH INCHARGE",
  "ASST BRANCH INCHARGE",
]);
const TECHNICAL_TL_DESIGNATIONS = new Set([
  "TECH LEAD",
  "ASST TECH LEAD",
  "TL",
]);
const TECHNICAL_TL_DESIGNATION_PATTERNS = [
  /\bTL\b/,
  /\bTECH(?:NICAL)?\s+LEAD\b/,
  /\bTEAM\s+LEAD\b/,
];
const BI_REVIEWER_PATTERNS = [
  /\bBI\b/,
  /\bBI\s+LOGIN\b/,
  /\bBUSINESS\s+INTELLIGENCE\b/,
];
const TECHNICAL_BRANCH_INCHARGE_PATTERNS = [
  /\bBRANCH\s+INCHARGE\b/,
];
const TECHNICAL_TOWER_MASTER_VISIT_DESIGNATIONS = new Set([
  ...TECHNICAL_BRANCH_INCHARGE_DESIGNATIONS,
  ...TECHNICAL_TL_DESIGNATIONS,
  "BI",
]);

const normalizeText = (value) => String(value || "").trim().toUpperCase();
const normalizeId = (value) => String(value || "").trim();
const getUserEmployeeIds = (sourceUser = {}) =>
  [
    sourceUser?.emp_id,
    sourceUser?.employee_id,
  ]
    .map(normalizeId)
    .filter(Boolean);
const parseSopData = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};
const isWorkflowTruthy = (value) => {
  const normalizedValue = normalizeText(value);
  return ["TRUE", "YES", "1", "TICKET CREATED", "COMPLETED", "DONE"].includes(
    normalizedValue
  );
};

const matchesDesignationPattern = (designation = "", exactSet = new Set(), patterns = []) => {
  const normalizedDesignation = normalizeText(designation);

  if (!normalizedDesignation) {
    return false;
  }

  if (exactSet.has(normalizedDesignation)) {
    return true;
  }

  return patterns.some((pattern) => pattern.test(normalizedDesignation));
};

export const isBiReviewerDesignation = (designation = "") =>
  matchesDesignationPattern(designation, new Set(["BI"]), BI_REVIEWER_PATTERNS);

export const isBiReviewerUser = (sourceUser = {}) =>
  isBiReviewerDesignation(sourceUser?.designation_name || sourceUser?.designation) ||
  isBiReviewerDesignation(sourceUser?.role);

const normalizeIdList = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeId).filter(Boolean);
  }

  if (typeof value === "string") {
    const text = value.trim();
    if (!text) {
      return [];
    }

    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map(normalizeId).filter(Boolean);
      }
    } catch {
      // Fall back to comma-separated parsing.
    }

    return text
      .split(",")
      .map(normalizeId)
      .filter(Boolean);
  }

  return [];
};

const createEmptyAccessScope = () => ({
  fullAccess: false,
  branchIds: new Set(),
  branchNames: new Set(),
  zoneIds: new Set(),
  zoneNames: new Set(),
  teamIds: new Set(),
  teamNames: new Set(),
});

const addIdsToScope = (scope, values = []) => {
  values
    .map(normalizeId)
    .filter(Boolean)
    .forEach((value) => scope.add(value));
};

const getModuleGrantScope = (sourceUser = {}, mainMenuKey = "", subMenuKey = "") => {
  const grant = getModuleGrant(sourceUser, mainMenuKey, subMenuKey);
  const scope = createEmptyAccessScope();

  if (!grant) {
    return scope;
  }

  const team = normalizeText(sourceUser?.team || sourceUser?.team_name);
  const department = normalizeText(sourceUser?.department);
  const isHeadOfficeTeam = HEAD_OFFICE_TEAMS.has(team) || HEAD_OFFICE_TEAMS.has(department);
  scope.fullAccess =
    normalizeText(grant.scope_type) === "GLOBAL" ||
    (isHeadOfficeTeam && normalizeText(grant.scope_type) !== "CUSTOM" && normalizeText(grant.scope_type) !== "SELF");
  addIdsToScope(scope.branchIds, grant.branch_ids || []);
  addIdsToScope(scope.zoneIds, grant.zone_ids || []);
  addIdsToScope(scope.teamIds, grant.team_ids || []);
  (grant.branch_names || [])
    .map(normalizeText)
    .filter(Boolean)
    .forEach((value) => scope.branchNames.add(value));
  (grant.zone_names || [])
    .map(normalizeText)
    .filter(Boolean)
    .forEach((value) => scope.zoneNames.add(value));
  (grant.team_names || [])
    .map(normalizeText)
    .filter(Boolean)
    .forEach((value) => scope.teamNames.add(value));
  return scope;
};

const mergeAccessScopes = (...scopes) => {
  const mergedScope = createEmptyAccessScope();

  scopes.forEach((scope) => {
    if (!scope) {
      return;
    }

    mergedScope.fullAccess = Boolean(mergedScope.fullAccess || scope.fullAccess);
    addIdsToScope(mergedScope.branchIds, Array.from(scope.branchIds || []));
    addIdsToScope(mergedScope.zoneIds, Array.from(scope.zoneIds || []));
    addIdsToScope(mergedScope.teamIds, Array.from(scope.teamIds || []));
    addIdsToScope(mergedScope.branchNames, Array.from(scope.branchNames || []).map(normalizeText));
    addIdsToScope(mergedScope.zoneNames, Array.from(scope.zoneNames || []).map(normalizeText));
    addIdsToScope(mergedScope.teamNames, Array.from(scope.teamNames || []).map(normalizeText));
  });

  return mergedScope;
};

export const getOnmAccessContext = (sourceUser = getAuthUser() || {}) => {
  const designation = normalizeText(sourceUser?.designation_name || sourceUser?.designation);
  const role = normalizeText(sourceUser?.role);
  const team = normalizeText(sourceUser?.team || sourceUser?.team_name);
  const department = normalizeText(sourceUser?.department);
  const primaryBranchId = normalizeId(
    sourceUser?.primary_branch_id || sourceUser?.branch_id
  );
  const primaryZoneId = normalizeId(
    sourceUser?.primary_zone_id || sourceUser?.zone_id
  );
  const crmBranchIds = [
    ...normalizeIdList(sourceUser?.crm_branch_ids),
    ...(primaryBranchId ? [primaryBranchId] : []),
  ];
  const crmZoneIds = [
    ...normalizeIdList(sourceUser?.crm_zone_ids),
    ...(primaryZoneId ? [primaryZoneId] : []),
  ];
  const crmBranchNames = [
    sourceUser?.branch,
    sourceUser?.branch_name,
    sourceUser?.primary_branch_name,
    sourceUser?.crm_branch_name,
  ]
    .map(normalizeText)
    .filter(Boolean);

  const uniqueBranchIds = [...new Set(crmBranchIds)];
  const uniqueBranchNames = [...new Set(crmBranchNames)];
  const uniqueZoneIds = [...new Set(crmZoneIds)];
  const isSuperPrivileged = SUPER_PRIVILEGED_ROLES.has(role);
  const isSales = SALES_IDENTIFIERS.has(team) || SALES_IDENTIFIERS.has(department);
  const hasOnmDepartmentAccess =
    ONM_IDENTIFIERS.has(team) || ONM_IDENTIFIERS.has(department);
  const hasTechnicalTeamAccess =
    TECHNICAL_IDENTIFIERS.has(team) || TECHNICAL_IDENTIFIERS.has(department);
  const hasPurchaseDepartmentAccess =
    PURCHASE_IDENTIFIERS.has(team) || PURCHASE_IDENTIFIERS.has(department);
  const hasTechnicalTowerMasterVisitAccess =
    hasTechnicalTeamAccess ||
    TECHNICAL_TOWER_MASTER_VISIT_DESIGNATIONS.has(designation) ||
    isBiReviewerDesignation(designation) ||
    matchesDesignationPattern(
      designation,
      TECHNICAL_BRANCH_INCHARGE_DESIGNATIONS,
      TECHNICAL_BRANCH_INCHARGE_PATTERNS
    ) ||
    matchesDesignationPattern(
      designation,
      TECHNICAL_TL_DESIGNATIONS,
      TECHNICAL_TL_DESIGNATION_PATTERNS
    );
  const isTechnicalBranchIncharge =
    hasTechnicalTeamAccess &&
    matchesDesignationPattern(
      designation,
      TECHNICAL_BRANCH_INCHARGE_DESIGNATIONS,
      TECHNICAL_BRANCH_INCHARGE_PATTERNS
    );
  const isTechnicalTlLead =
    hasTechnicalTeamAccess &&
    matchesDesignationPattern(
      designation,
      TECHNICAL_TL_DESIGNATIONS,
      TECHNICAL_TL_DESIGNATION_PATTERNS
    );
  const isBiReviewer = isBiReviewerUser({
    designation_name: designation,
    role,
  });
  const hasDashboardModuleAccess = hasModulePermission(
    sourceUser,
    "ONM",
    "DASHBOARD",
    "view"
  );
  const hasTowerMasterModuleAccess = hasModulePermission(
    sourceUser,
    "ONM",
    "TOWER_MASTER_DATA",
    "view"
  );
  const hasTowerMaintenanceModuleAccess = hasModulePermission(
    sourceUser,
    "ONM",
    "TOWER_MAINTENANCE",
    "view"
  );
  const hasAccessPointMasterModuleAccess = hasModulePermission(
    sourceUser,
    "ONM",
    "ACCESS_POINT_MASTER",
    "view"
  );
  const hasTowerDownTrackerModuleAccess = hasModulePermission(
    sourceUser,
    "ONM",
    "TOWER_DOWN_TRACKER",
    "view"
  );
  const hasTowerDownTrackerDashboardModuleAccess = hasModulePermission(
    sourceUser,
    "ONM",
    "TOWER_DOWN_TRACKER_DASHBOARD",
    "view"
  );
  const dashboardModuleScope = getModuleGrantScope(sourceUser, "ONM", "DASHBOARD");
  const towerMasterModuleScope = getModuleGrantScope(sourceUser, "ONM", "TOWER_MASTER_DATA");
  const towerMaintenanceModuleScope = getModuleGrantScope(sourceUser, "ONM", "TOWER_MAINTENANCE");
  const accessPointMasterModuleScope = getModuleGrantScope(sourceUser, "ONM", "ACCESS_POINT_MASTER");
  const towerDownTrackerModuleScope = getModuleGrantScope(sourceUser, "ONM", "TOWER_DOWN_TRACKER");
  const towerDownTrackerDashboardModuleScope = getModuleGrantScope(sourceUser, "ONM", "TOWER_DOWN_TRACKER_DASHBOARD");
  const hasExplicitModuleAccess =
    hasDashboardModuleAccess ||
    hasTowerMasterModuleAccess ||
    hasTowerMaintenanceModuleAccess ||
    hasAccessPointMasterModuleAccess ||
    hasTowerDownTrackerModuleAccess ||
    hasTowerDownTrackerDashboardModuleAccess;

  return {
    designation,
    role,
    team,
    department,
    crmBranchIds: uniqueBranchIds,
    crmBranchNames: uniqueBranchNames,
    crmZoneIds: uniqueZoneIds,
    isSuperPrivileged,
    isSales,
    hasOnmDepartmentAccess,
    hasTechnicalTeamAccess,
    hasPurchaseDepartmentAccess,
    hasTechnicalTowerMasterVisitAccess,
    isTechnicalBranchIncharge,
    isTechnicalTlLead,
    isBiReviewer,
    hasDashboardModuleAccess,
    hasTowerMasterModuleAccess,
    hasTowerMaintenanceModuleAccess,
    hasAccessPointMasterModuleAccess,
    hasTowerDownTrackerModuleAccess,
    hasTowerDownTrackerDashboardModuleAccess,
    hasExplicitModuleAccess,
    dashboardModuleScope,
    towerMasterModuleScope,
    towerMaintenanceModuleScope,
    accessPointMasterModuleScope,
    towerDownTrackerModuleScope,
    towerDownTrackerDashboardModuleScope,
  };
};

export const hasScopedOnmModulePermission = (
  sourceUser = getAuthUser() || {},
  subMenuKey = "",
  action = "view",
  legacyAccess = false
) => {
  const role = normalizeText(sourceUser?.role);

  if (SUPER_PRIVILEGED_ROLES.has(role)) {
    return true;
  }

  if (getModuleGrant(sourceUser, "ONM", subMenuKey)) {
    return hasModulePermission(sourceUser, "ONM", subMenuKey, action);
  }

  return Boolean(legacyAccess);
};

const hasGrantActionPermission = (grant = null, action = "view") => {
  if (!grant) return false;

  const normalizedAction = normalizeText(action || "view");

  if (normalizedAction === "CREATE") return Boolean(grant.can_create);
  if (normalizedAction === "EDIT") return Boolean(grant.can_edit);
  if (normalizedAction === "DELETE") return Boolean(grant.can_delete);
  if (normalizedAction === "EXPORT") return Boolean(grant.can_export);
  if (normalizedAction === "APPROVE") return Boolean(grant.can_approve);

  return Boolean(
    grant.can_view ||
      grant.can_edit ||
      grant.can_delete ||
      grant.can_create ||
      grant.can_export ||
      grant.can_approve
  );
};

export const hasExplicitOnmModulePermission = (
  sourceUser = getAuthUser() || {},
  subMenuKey = "",
  action = "view"
) => {
  const role = normalizeText(sourceUser?.role);

  if (SUPER_PRIVILEGED_ROLES.has(role)) {
    return true;
  }

  return hasGrantActionPermission(getModuleGrant(sourceUser, "ONM", subMenuKey), action);
};

export const canAccessOnm = (sourceUser = getAuthUser() || {}) => {
  const context = getOnmAccessContext(sourceUser);

  if (context.isSuperPrivileged) {
    return true;
  }

  if (context.hasOnmDepartmentAccess) {
    return true;
  }

  if (context.hasTechnicalTowerMasterVisitAccess) {
    return true;
  }

  if (context.hasExplicitModuleAccess) {
    return true;
  }

  return false;
};

export const canAccessOnmDashboard = (sourceUser = getAuthUser() || {}) => {
  const context = getOnmAccessContext(sourceUser);

  if (context.isSuperPrivileged || context.hasOnmDepartmentAccess) {
    return true;
  }

  if (context.isSales) {
    return false;
  }

  if (context.hasDashboardModuleAccess) {
    return true;
  }

  if (context.hasTechnicalTowerMasterVisitAccess) {
    return context.crmBranchIds.length > 0;
  }

  if (context.hasDashboardModuleAccess || context.hasTowerMaintenanceModuleAccess) {
    return true;
  }

  return false;
};

export const resolveAccessibleOnmBranchNames = (
  allBranches = [],
  sourceUser = getAuthUser() || {}
) => {
  const context = getOnmAccessContext(sourceUser);

  if (context.isSuperPrivileged || context.hasOnmDepartmentAccess) {
    return null;
  }

  const allowedBranchIds = new Set(context.crmBranchIds.map(normalizeId));
  const allowedBranchNames = new Set();

  allBranches.forEach((branch) => {
    const branchId = normalizeId(
      branch?.branch_id ?? branch?.id ?? branch?.branchId
    );

    if (!allowedBranchIds.has(branchId)) {
      return;
    }

    [
      branch?.name,
      branch?.branch_name,
      branch?.short_name,
      branch?.shortName,
    ]
      .map(normalizeText)
      .filter(Boolean)
      .forEach((name) => allowedBranchNames.add(name));
  });

  return allowedBranchNames;
};

const resolveAccessibleOnmScope = (
  allBranches = [],
  sourceUser = getAuthUser() || {},
  explicitScope = null
) => {
  const context = getOnmAccessContext(sourceUser);

  if (context.isSuperPrivileged || context.hasOnmDepartmentAccess) {
    return null;
  }

  if (explicitScope) {
    const scopedAccess = mergeAccessScopes(explicitScope);
    if (scopedAccess.fullAccess) {
      return null;
    }

    allBranches.forEach((branch) => {
      const branchId = normalizeId(
        branch?.branch_id ?? branch?.id ?? branch?.branchId
      );

      if (!scopedAccess.branchIds.has(branchId)) {
        return;
      }

      [
        branch?.name,
        branch?.branch_name,
        branch?.short_name,
        branch?.shortName,
      ]
        .map(normalizeText)
        .filter(Boolean)
        .forEach((name) => scopedAccess.branchNames.add(name));
    });

    return scopedAccess;
  }

  const allowedBranchIds = new Set(context.crmBranchIds.map(normalizeId));
  const allowedBranchNames = new Set(
    (context.crmBranchNames || []).map(normalizeText).filter(Boolean)
  );

  allBranches.forEach((branch) => {
    const branchId = normalizeId(
      branch?.branch_id ?? branch?.id ?? branch?.branchId
    );

    if (!allowedBranchIds.has(branchId)) {
      return;
    }

    [
      branch?.name,
      branch?.branch_name,
      branch?.short_name,
      branch?.shortName,
    ]
      .map(normalizeText)
      .filter(Boolean)
      .forEach((name) => allowedBranchNames.add(name));
  });

  return {
    branchIds: allowedBranchIds,
    branchNames: allowedBranchNames,
    zoneIds: new Set(context.crmZoneIds.map(normalizeId)),
    zoneNames: new Set(),
    teamIds: new Set(),
    teamNames: new Set(),
  };
};

const getNormalizedRowBranchNames = (row = {}) => {
  const rawValues = [
    row?.branch,
    row?.branch_name,
    row?.sopData?.branch_name,
    row?.sop_data?.branch_name,
  ];

  return rawValues.map(normalizeText).filter(Boolean);
};

const getNormalizedRowBranchId = (row = {}) =>
  normalizeId(
    row?.branch_id ??
      row?.sopData?.branch_id ??
      row?.sop_data?.branch_id
  );

const getNormalizedRowZoneId = (row = {}) =>
  normalizeId(
    row?.zone_id ??
      row?.sopData?.zone_id ??
      row?.sop_data?.zone_id
  );

const getNormalizedRowTeamId = (row = {}) =>
  normalizeId(
    row?.team_id ??
      row?.teamId ??
      row?.team?.id ??
      row?.employee?.team_id ??
      row?.sopData?.team_id ??
      row?.sop_data?.team_id
  );

const getNormalizedRowTeamNames = (row = {}) => {
  const rawValues = [
    row?.team,
    row?.team_name,
    row?.department,
    row?.department_name,
    row?.employee?.team_name,
    row?.sopData?.team_name,
    row?.sop_data?.team_name,
  ];

  return rawValues
    .map((value) => (typeof value === "object" ? value?.team_name || value?.name : value))
    .map(normalizeText)
    .filter(Boolean);
};

const hasTicketCreatedForRow = (row = {}) => {
  const sopData = parseSopData(row?.sopData || row?.sop_data);

  return (
    isWorkflowTruthy(sopData.ticket_created) ||
    Boolean(
      String(sopData.ticket_id || "").trim() &&
        String(sopData.ticket_date || "").trim()
    )
  );
};

const hasTechnicalUpdateCompletedForRow = (row = {}) => {
  const sopData = parseSopData(row?.sopData || row?.sop_data);
  return isWorkflowTruthy(sopData.technical_update_completed);
};

const isRowOwnedByTechnicalUser = (row = {}, sourceUser = {}) => {
  const userEmployeeIds = new Set(getUserEmployeeIds(sourceUser));

  if (userEmployeeIds.size === 0) {
    return false;
  }

  const sopData = parseSopData(row?.sopData || row?.sop_data);
  const rowEmployeeIds = [
    sopData.ticket_raised_emp_id,
    sopData.technical_update_completed_by,
    sopData.tower_visit_emp_id_1,
    sopData.tower_visit_emp_id_2,
  ]
    .map(normalizeId)
    .filter(Boolean);

  return rowEmployeeIds.some((employeeId) => userEmployeeIds.has(employeeId));
};

const doesRowMatchAccessibleOnmScope = (
  row = {},
  accessibleScope = null,
  { allowZoneFallback = false } = {}
) => {
  if (
    !accessibleScope ||
    (
      accessibleScope.branchNames.size === 0 &&
      accessibleScope.branchIds.size === 0 &&
      accessibleScope.zoneIds.size === 0 &&
      accessibleScope.zoneNames.size === 0 &&
      accessibleScope.teamIds.size === 0 &&
      accessibleScope.teamNames.size === 0
    )
  ) {
    return false;
  }

  const rowBranchId = getNormalizedRowBranchId(row);
  const rowZoneId = getNormalizedRowZoneId(row);
  const rowZoneName = normalizeText(row?.zone ?? row?.zone_name);
  const rowBranchNames = getNormalizedRowBranchNames(row);
  const rowTeamId = getNormalizedRowTeamId(row);
  const rowTeamNames = getNormalizedRowTeamNames(row);
  const hasScopedBranches =
    accessibleScope.branchIds.size > 0 || accessibleScope.branchNames.size > 0;
  const hasScopedTeams =
    accessibleScope.teamIds.size > 0 || accessibleScope.teamNames.size > 0;

  if (rowTeamId && accessibleScope.teamIds.has(rowTeamId)) {
    return true;
  }

  if (rowTeamNames.some((teamName) => accessibleScope.teamNames.has(teamName))) {
    return true;
  }

  if (rowBranchId && accessibleScope.branchIds.has(rowBranchId)) {
    return true;
  }

  if (rowBranchNames.some((branchName) => accessibleScope.branchNames.has(branchName))) {
    return true;
  }

  if (hasScopedBranches || hasScopedTeams) {
    return false;
  }

  if (allowZoneFallback && rowZoneId && accessibleScope.zoneIds.has(rowZoneId)) {
    return true;
  }

  if (rowZoneName && accessibleScope.zoneNames.has(rowZoneName)) {
    return true;
  }

  return false;
};

export const filterOnmRowsByAccess = (
  rows = [],
  allBranches = [],
  sourceUser = getAuthUser() || {}
) => {
  const context = getOnmAccessContext(sourceUser);
  const explicitScope =
    context.hasDashboardModuleAccess || context.hasTowerMaintenanceModuleAccess
      ? mergeAccessScopes(context.dashboardModuleScope, context.towerMaintenanceModuleScope)
      : null;

  if (explicitScope?.fullAccess) {
    return rows;
  }

  if (context.isSuperPrivileged || context.hasOnmDepartmentAccess) {
    return rows;
  }

  if (context.isSales) {
    return [];
  }

  const accessibleScope = resolveAccessibleOnmScope(allBranches, sourceUser, explicitScope);

  return rows.filter((row) => {
    if (explicitScope) {
      return doesRowMatchAccessibleOnmScope(row, accessibleScope, { allowZoneFallback: false });
    }

    if (!hasTicketCreatedForRow(row)) {
      return false;
    }

    if (!doesRowMatchAccessibleOnmScope(row, accessibleScope, { allowZoneFallback: false })) {
      return false;
    }

    if (context.hasTechnicalTeamAccess && isRowOwnedByTechnicalUser(row, sourceUser)) {
      return true;
    }

    return hasTechnicalUpdateCompletedForRow(row) || hasTicketCreatedForRow(row);
  });
};

export const filterTowerMasterRowsByAccess = (
  rows = [],
  allBranches = [],
  sourceUser = getAuthUser() || {}
) => {
  const context = getOnmAccessContext(sourceUser);
  const explicitScope = context.hasTowerMasterModuleAccess
    ? mergeAccessScopes(context.dashboardModuleScope, context.towerMasterModuleScope)
    : null;

  if (explicitScope?.fullAccess) {
    return rows;
  }

  if (context.isSuperPrivileged || context.hasOnmDepartmentAccess) {
    return rows;
  }

  if (
    context.isSales ||
    (!context.hasTechnicalTowerMasterVisitAccess && !explicitScope)
  ) {
    return [];
  }

  const accessibleScope = resolveAccessibleOnmScope(allBranches, sourceUser, explicitScope);

  return rows.filter((row) =>
    doesRowMatchAccessibleOnmScope(row, accessibleScope, { allowZoneFallback: false })
  );
};

export const filterAccessPointMasterRowsByAccess = (
  rows = [],
  allBranches = [],
  sourceUser = getAuthUser() || {}
) => {
  const context = getOnmAccessContext(sourceUser);
  const explicitScope = context.hasAccessPointMasterModuleAccess
    ? mergeAccessScopes(context.accessPointMasterModuleScope)
    : null;

  if (explicitScope?.fullAccess) {
    return rows;
  }

  if (context.isSuperPrivileged || context.hasOnmDepartmentAccess) {
    return rows;
  }

  if (context.isSales || !explicitScope) {
    return [];
  }

  const accessibleScope = resolveAccessibleOnmScope(allBranches, sourceUser, explicitScope);

  return rows.filter((row) =>
    doesRowMatchAccessibleOnmScope(row, accessibleScope, { allowZoneFallback: false })
  );
};

export const canAccessTowerMasterRow = (
  row,
  allBranches = [],
  sourceUser = getAuthUser() || {}
) => filterTowerMasterRowsByAccess([row], allBranches, sourceUser).length > 0;

export const canAccessAccessPointMasterRow = (
  row,
  allBranches = [],
  sourceUser = getAuthUser() || {}
) => filterAccessPointMasterRowsByAccess([row], allBranches, sourceUser).length > 0;

export const canAccessOnmRow = (
  row,
  allBranches = [],
  sourceUser = getAuthUser() || {}
) => filterOnmRowsByAccess([row], allBranches, sourceUser).length > 0;
